/**
 * Two-month stay picker for Maison Grès product pages.
 * Updates quantity (nights), line item properties, and price summary.
 * Respects blocked nights from product metafield custom.blocked_stay_dates.
 */
(function () {
  const Av = () => window.MaisonAvailability;

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toYMD(d) {
    return Av()?.toYMD(d) || `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  function parseYMD(s) {
    return Av()?.parseYMD(s) || null;
  }

  function nightsBetween(inDate, outDate) {
    if (!inDate || !outDate) return 0;
    const ms = outDate.getTime() - inDate.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }

  function formatMoney(cents, currencyCode) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  function addMonths(date, delta) {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
  }

  function readBlockedRules(root) {
    const rulesEl = root.querySelector('[data-maison-blocked-rules]');
    if (!rulesEl || !Av()) return new Set();
    let raw = '';
    try {
      raw = JSON.parse(rulesEl.textContent || '""');
    } catch {
      raw = rulesEl.textContent || '';
    }
    return Av().expandRules(raw);
  }

  class StayPicker {
    constructor(root) {
      this.root = root;
      this.monthAnchor = startOfMonth(new Date());
      this.checkIn = null;
      this.checkOut = null;
      this.currency = root.dataset.currency || 'USD';
      this.unitCents = Number(root.dataset.unitCents || '0');
      this.blockedSet = readBlockedRules(root);
      this.unavailableMessage =
        root.dataset.availabilityError || 'Those dates are not available. Please choose different dates.';

      this.elMonths = root.querySelector('[data-cal-months]');
      this.elCheckInInput = root.querySelector('[data-input-checkin]');
      this.elCheckOutInput = root.querySelector('[data-input-checkout]');
      this.elPrev = root.querySelector('[data-cal-prev]');
      this.elNext = root.querySelector('[data-cal-next]');
      this.elSummaryNights = root.querySelector('[data-summary-nights]');
      this.elSummaryTotal = root.querySelector('[data-summary-total]');
      this.elSummaryAvg = root.querySelector('[data-summary-avg]');
      this.elSummaryPackage = root.querySelector('[data-summary-package]');
      this.elQty = root.querySelector('[data-qty]');
      this.elPropIn = root.querySelector('[data-prop-checkin]');
      this.elPropOut = root.querySelector('[data-prop-checkout]');
      this.elPropGuests = root.querySelector('[data-prop-guests]');
      this.elGuests = root.querySelector('[data-guests]');
      this.elError = root.querySelector('[data-maison-availability-error]');
      this.elReserve = root.querySelector('.maison-product__reserve');

      this.bind();
      this.render();
      this.syncFromInputs();
      this.updateReserveState();
    }

    bind() {
      if (this.elPrev) {
        this.elPrev.addEventListener('click', () => {
          this.monthAnchor = addMonths(this.monthAnchor, -1);
          this.render();
        });
      }
      if (this.elNext) {
        this.elNext.addEventListener('click', () => {
          this.monthAnchor = addMonths(this.monthAnchor, 1);
          this.render();
        });
      }
      if (this.elCheckInInput) {
        this.elCheckInInput.addEventListener('change', () => {
          this.checkIn = parseYMD(this.elCheckInInput.value);
          this.ensureOrder();
          this.refreshPricing();
        });
      }
      if (this.elCheckOutInput) {
        this.elCheckOutInput.addEventListener('change', () => {
          this.checkOut = parseYMD(this.elCheckOutInput.value);
          this.ensureOrder();
          this.refreshPricing();
        });
      }
      if (this.elGuests) {
        this.elGuests.addEventListener('change', () => this.syncProps());
      }
    }

    showError(message) {
      if (!this.elError) return;
      if (message) {
        this.elError.textContent = message;
        this.elError.hidden = false;
      } else {
        this.elError.textContent = '';
        this.elError.hidden = true;
      }
    }

    isDayUnavailable(day) {
      return Av()?.isDayUnavailable(day, this.blockedSet) || false;
    }

    rangeIsBlocked(checkIn, checkOut) {
      return Av()?.stayHasBlockedNight(checkIn, checkOut, this.blockedSet) || false;
    }

    ensureOrder() {
      if (this.checkIn && this.checkOut && this.checkOut <= this.checkIn) {
        this.checkOut = Av()?.addDays(this.checkIn, 1) || null;
        if (this.elCheckOutInput && this.checkOut) {
          this.elCheckOutInput.value = toYMD(this.checkOut);
        }
      }
    }

    syncFromInputs() {
      this.checkIn = parseYMD(this.elCheckInInput?.value);
      this.checkOut = parseYMD(this.elCheckOutInput?.value);
      this.refreshPricing();
    }

    monthMatrix(anchor) {
      const first = startOfMonth(anchor);
      const year = first.getFullYear();
      const month = first.getMonth();
      const startWeekday = first.getDay();
      const mondayIndex = (startWeekday + 6) % 7;
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      const cells = [];
      for (let i = 0; i < mondayIndex; i += 1) cells.push(null);
      for (let d = 1; d <= daysInMonth; d += 1) {
        cells.push(new Date(year, month, d));
      }
      return { label: first.toLocaleString(undefined, { month: 'long', year: 'numeric' }), cells };
    }

    renderMonth(title, cells) {
      const weekHeader = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        .map((d) => `<div class="maison-cal__dow">${d}</div>`)
        .join('');

      const dayButtons = cells
        .map((day) => {
          if (!day) return `<div class="maison-cal__cell maison-cal__cell--empty"></div>`;
          const ymd = toYMD(day);
          const unavailable = this.isDayUnavailable(day);
          const isIn = this.checkIn && ymd === toYMD(this.checkIn);
          const isOut = this.checkOut && ymd === toYMD(this.checkOut);
          const inRange =
            this.checkIn && this.checkOut && day > this.checkIn && day < this.checkOut;
          let cls = 'maison-cal__day';
          if (unavailable) cls += ' is-unavailable';
          if (isIn) cls += ' is-checkin';
          if (isOut) cls += ' is-checkout';
          if (inRange) cls += ' is-range';
          const disabled = unavailable ? ' disabled aria-disabled="true"' : '';
          return `<button type="button" class="${cls}" data-day="${ymd}" aria-pressed="${isIn || isOut}"${disabled}>${day.getDate()}</button>`;
        })
        .join('');

      return `
        <div class="maison-cal__month">
          <div class="maison-cal__month-title">${title}</div>
          <div class="maison-cal__grid">${weekHeader}${dayButtons}</div>
        </div>
      `;
    }

    render() {
      if (!this.elMonths) return;
      const m0 = this.monthMatrix(this.monthAnchor);
      const m1 = this.monthMatrix(addMonths(this.monthAnchor, 1));
      this.elMonths.innerHTML = this.renderMonth(m0.label, m0.cells) + this.renderMonth(m1.label, m1.cells);

      this.elMonths.querySelectorAll('[data-day]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          const ymd = btn.getAttribute('data-day');
          const clicked = parseYMD(ymd);
          if (!clicked || this.isDayUnavailable(clicked)) return;

          if (!this.checkIn || (this.checkIn && this.checkOut)) {
            this.checkIn = clicked;
            this.checkOut = null;
            if (this.elCheckInInput) this.elCheckInInput.value = ymd;
            if (this.elCheckOutInput) this.elCheckOutInput.value = '';
            this.showError('');
          } else if (clicked <= this.checkIn) {
            this.checkIn = clicked;
            if (this.elCheckInInput) this.elCheckInInput.value = ymd;
            this.checkOut = null;
            if (this.elCheckOutInput) this.elCheckOutInput.value = '';
            this.showError('');
          } else {
            const proposedOut = clicked;
            if (this.rangeIsBlocked(this.checkIn, proposedOut)) {
              this.showError(this.unavailableMessage);
              return;
            }
            this.checkOut = proposedOut;
            if (this.elCheckOutInput) this.elCheckOutInput.value = ymd;
            this.showError('');
          }
          this.render();
          this.refreshPricing();
        });
      });
    }

    updateReserveState() {
      const blocked =
        this.checkIn &&
        this.checkOut &&
        this.rangeIsBlocked(this.checkIn, this.checkOut);
      const incomplete = !this.checkIn || !this.checkOut;
      if (this.elReserve) {
        this.elReserve.disabled = Boolean(blocked || incomplete);
      }
    }

    refreshPricing() {
      const nights = nightsBetween(this.checkIn, this.checkOut);
      if (this.elSummaryNights) {
        this.elSummaryNights.textContent = String(Math.max(1, nights || 1));
      }

      const qty = Math.max(1, nights || 1);
      if (this.elQty) this.elQty.value = String(qty);

      const total = this.unitCents * qty;
      if (this.elSummaryTotal) {
        this.elSummaryTotal.textContent = formatMoney(total, this.currency);
      }
      if (this.elSummaryPackage) {
        this.elSummaryPackage.textContent = `${formatMoney(total, this.currency)} for ${qty} night${qty === 1 ? '' : 's'}`;
      }
      if (this.elSummaryAvg) {
        this.elSummaryAvg.textContent = formatMoney(this.unitCents, this.currency);
      }

      if (this.checkIn && this.checkOut && this.rangeIsBlocked(this.checkIn, this.checkOut)) {
        this.showError(this.unavailableMessage);
      } else if (this.checkIn && this.checkOut) {
        this.showError('');
      }

      this.syncProps();
      this.updateReserveState();
    }

    syncProps() {
      const guests = this.elGuests?.value || '1';
      if (this.elPropIn && this.checkIn) this.elPropIn.value = toYMD(this.checkIn);
      if (this.elPropOut && this.checkOut) this.elPropOut.value = toYMD(this.checkOut);
      if (this.elPropGuests) this.elPropGuests.value = `${guests} guests`;
    }
  }

  function initCalendars(scope) {
    const roots = (scope || document).querySelectorAll('[data-maison-calendar-root]');
    roots.forEach((root) => {
      if (root.dataset.maisonCalendarReady === 'true') return;
      try {
        new StayPicker(root);
        root.dataset.maisonCalendarReady = 'true';
      } catch (e) {
        console.error('Maison calendar init failed', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCalendars());
  } else {
    initCalendars();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCalendars(event.target);
  });
})();
